import React from 'react';
import { Flex, Button, Checkbox } from 'design-system-web';
import { Alert } from 'components/lds';
import routes from 'lane-shared/config/routes';
import history from 'helpers/history';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { TranslationKeys } from 'localization';
import { useTranslation } from 'react-i18next';
import { H5 } from 'components/typography';
import { useWorkOrdersPaymentsManagedExternallyEnabled } from 'react-hooks';
import styles from './BillingTab.scss';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';

import { getInvoicingOptionsEnabledValue } from '../../../../helpers/getInvoicingOptionsEnabledValue';

const TRANSLATION_KEYS: { [key: string]: TranslationKeys } = Object.freeze({
  billingPaymentsHeader:
    'web.admin.serviceRequest.settings.billing.billingPayments',
  productsServicesButton:
    'web.admin.serviceRequest.settings.billing.productsAndServicesButton',
  invoicesHeader: 'web.admin.serviceRequest.settings.billing.invoices',
  enableInvoicingCheckbox:
    'web.admin.serviceRequest.settings.billing.enableInvoicing',
  enableInvoicingDisabledWarning:
    'web.admin.serviceRequest.settings.billing.enableInvoicing.disabled.warning',
  paymentsManagedExternallyCheckbox:
    'web.admin.serviceRequest.settings.billing.paymentsManagedExternally',
  chargeVerificationHeader:
    'web.admin.serviceRequest.settings.billing.chargeVerification.header',
  chargeVerificationCheckbox:
    'web.admin.serviceRequest.settings.billing.chargeVerification.checkbox',
  chargeVerificationHelperText:
    'web.admin.serviceRequest.settings.billing.chargeVerification.helperText',
});

type ChargeVerificationCheckbox = {
  isChecked: boolean;
  isDisabled: boolean;
  onChange: (value: boolean) => void;
};

type Props = {
  channel: Channel;
  settings: WorkOrderModuleSettings;
  isBillingInvoicingDisabled: boolean;
  isPaymentsManagedExternally: boolean;
  onSettingsUpdated: (settings: WorkOrderModuleSettings) => void;
  onPaymentsManagedExternallyUpdated: (isEnabled: boolean) => void;
  chargeVerification?: ChargeVerificationCheckbox;
};

export const BillingTab = ({
  channel,
  settings,
  isBillingInvoicingDisabled,
  isPaymentsManagedExternally,
  onSettingsUpdated,
  onPaymentsManagedExternallyUpdated,
  chargeVerification,
}: Props) => {
  const { t } = useTranslation();

  const isInvoiceOptionsEnabled = getInvoicingOptionsEnabledValue(
    isBillingInvoicingDisabled,
    settings?.isInvoiceOptionsEnabled
  );

  const isPaymentsManagedExternallyFFEnabled =
    useWorkOrdersPaymentsManagedExternallyEnabled();

  return (
    <Flex direction="column" gap={7} className={styles.billingTab}>
      <Flex direction="column" align="self-start">
        <H5 className={styles.header}>
          {t(TRANSLATION_KEYS.billingPaymentsHeader)}
        </H5>
        <Button
          variant="secondary"
          size="large"
          onClick={() =>
            history.push(
              routes.channelAdminWorkOrdersServiceRequestsBillingSettings.replace(
                ':id',
                channel._id
              )
            )
          }
        >
          {t(TRANSLATION_KEYS.productsServicesButton)}
        </Button>
      </Flex>
      <Flex direction="column" gap={4}>
        <H5 className={styles.header}>{t(TRANSLATION_KEYS.invoicesHeader)}</H5>
        {isBillingInvoicingDisabled && (
          <Alert type="warning" className={styles.alert}>
            {t(TRANSLATION_KEYS.enableInvoicingDisabledWarning)}
          </Alert>
        )}
        <Checkbox
          selected={isInvoiceOptionsEnabled}
          value={isInvoiceOptionsEnabled}
          onChange={() =>
            onSettingsUpdated({
              ...settings,
              isInvoiceOptionsEnabled: !isInvoiceOptionsEnabled,
            })
          }
          disabled={isBillingInvoicingDisabled}
          text={t(TRANSLATION_KEYS.enableInvoicingCheckbox)}
          interfaceStyle="light"
          testId="wo-invoicing-checkbox"
        />
        {isInvoiceOptionsEnabled && isPaymentsManagedExternallyFFEnabled && (
          <Checkbox
            selected={isPaymentsManagedExternally}
            value={isPaymentsManagedExternally}
            onChange={() =>
              onPaymentsManagedExternallyUpdated(!isPaymentsManagedExternally)
            }
            text={t(TRANSLATION_KEYS.paymentsManagedExternallyCheckbox)}
            interfaceStyle="light"
            testId="wo-payments-managed-externally-checkbox"
          />
        )}
      </Flex>
      {chargeVerification && (
        <Flex direction="column" gap={4}>
          <H5 className={styles.header}>
            {t(TRANSLATION_KEYS.chargeVerificationHeader)}
          </H5>
          <Checkbox
            selected={chargeVerification.isChecked}
            value={chargeVerification.isChecked}
            onChange={() =>
              chargeVerification.onChange(!chargeVerification.isChecked)
            }
            disabled={chargeVerification.isDisabled}
            text={t(TRANSLATION_KEYS.chargeVerificationCheckbox)}
            description={t(TRANSLATION_KEYS.chargeVerificationHelperText)}
            interfaceStyle="light"
            testId="wo-charge-verification-checkbox"
          />
        </Flex>
      )}
    </Flex>
  );
};
